/**
 * LockedSection — inline gated card for a section that lives inside an
 * otherwise-accessible page (e.g. "Heatmap anual" inside Calendar,
 * "Recomendaciones IA" inside Profile/Recommendations).
 *
 * Mirrors ``LockedPage`` (value prop + plan/addon/pack recommendation
 * cards) but compact and embeddable. Pulls label + bullets +
 * recommendations from the catalog via ``useFeatureRecommendation``.
 *
 * Usage:
 *   <LockedSection featureKey="calendar_heatmap"
 *                  titleEs="Heatmap anual"
 *                  bullets={[{es:"…", en:"…"}, …]}>
 *     <RealComponent />   // shown when entitled; locked card otherwise
 *   </LockedSection>
 *
 * When ``children`` is omitted, the component always renders the
 * locked card (caller decided externally when to gate).
 */
import { Card } from "./ui/card";
import { Lock, Sparkles } from "lucide-react";
import { useLang } from "../contexts/LangContext";
import { tx } from "../i18n";
import { useEntitlements } from "../hooks/useEntitlements";
import { useFeatureRecommendation } from "../hooks/useFeatureRecommendation";
import { RecommendationCards } from "./RecommendationCards";

export function LockedSection({ featureKey, titleEs, titleEn, titlePt, bullets = [], children }) {
  const { lang } = useLang();
  const { has, isLoading } = useEntitlements();
  const { data: rec } = useFeatureRecommendation(featureKey);

  // iter-170ad — Drop-in replacement for the deleted ``FeatureGate``:
  // render children if the user has the feature, else the locked
  // upsell card. Without children we always render the locked card
  // (caller already decided to gate).
  if (children !== undefined) {
    if (isLoading) return null;
    if (has(featureKey)) return children;
  }

  const resolvedTitle = tx(
    lang,
    titleEn || rec?.feature?.label_en || featureKey,
    titleEs || rec?.feature?.label_es || featureKey,
    titlePt || rec?.feature?.label_pt || featureKey,
  );

  return (
    <Card
      className="p-6 rounded-2xl border-sky-200 bg-gradient-to-br from-sky-50/70 to-white space-y-4"
      data-testid={`locked-section-${featureKey}`}
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center flex-shrink-0">
          <Lock className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-base font-bold text-slate-900">{resolvedTitle}</h3>
          <p className="text-xs text-slate-600 leading-snug mt-0.5">
            {tx(lang,
              "Your current plan doesn't include this section.",
              "Tu plan actual no incluye esta sección.",
              "Seu plano atual não inclui esta seção.")}
          </p>
        </div>
      </div>
      {bullets.length > 0 && (
        <ul className="space-y-1 text-sm pl-13" data-testid={`locked-section-bullets-${featureKey}`}>
          {bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2">
              <Sparkles className="h-3.5 w-3.5 text-sky-600 shrink-0 mt-0.5" />
              <span className="text-slate-700">{tx(lang, b.en, b.es, b.pt)}</span>
            </li>
          ))}
        </ul>
      )}
      <div>
        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
          {tx(lang, "Unlock with", "Activala con", "Ative com")}
        </p>
        <RecommendationCards featureKey={featureKey} />
      </div>
    </Card>
  );
}
